/**
 * BioGaia FR Professional — HCP authentication helpers
 * Validates French healthcare license numbers (RPPS / ADELI) and manages registration data.
 */
(function () {
  'use strict';

  var RPPS_PROFESSION_MAP = {
    '1': 'physician',
    '2': 'pharmacist',
    '3': 'dentist',
    '4': 'midwife',
    '8': 'nurse'
  };

  var PROFESSION_REDIRECTS = {
    midwife: '/pages/child-health',
    dentist: '/pages/dental-pro',
    pediatrician: '/pages/child-health',
    dietitian: '/pages/biogaia-products'
  };

  function sanitizeLicense(value) {
    return String(value || '').replace(/\s+/g, '').replace(/-/g, '');
  }

  function validateRpps(license) {
    if (!/^\d{11}$/.test(license)) {
      return { valid: false, error: 'rpps_format' };
    }

    var body = parseInt(license.substring(0, 9), 10);
    var key = parseInt(license.substring(9, 11), 10);
    var expectedKey = 97 - (body % 97);

    if (key !== expectedKey) {
      return { valid: false, error: 'rpps_checksum' };
    }

    var detectedProfession = RPPS_PROFESSION_MAP[license.charAt(0)] || 'other';
    return { valid: true, type: 'rpps', detectedProfession: detectedProfession };
  }

  function validateAdeli(license) {
    if (!/^\d{9}$/.test(license)) {
      return { valid: false, error: 'adeli_format' };
    }
    return { valid: true, type: 'adeli', detectedProfession: null };
  }

  function validateLicense(license, profession) {
    var sanitized = sanitizeLicense(license);

    if (!sanitized) {
      return { valid: false, error: 'required' };
    }

    var rppsResult = validateRpps(sanitized);
    if (rppsResult.valid) {
      if (profession && rppsResult.detectedProfession !== 'other' && profession !== rppsResult.detectedProfession && profession !== 'pediatrician') {
        return {
          valid: true,
          type: 'rpps',
          detectedProfession: rppsResult.detectedProfession,
          warning: 'profession_mismatch'
        };
      }
      return rppsResult;
    }

    var adeliResult = validateAdeli(sanitized);
    if (adeliResult.valid) {
      return adeliResult;
    }

    return { valid: false, error: 'invalid_license' };
  }

  function collectProfessions(primaryProfession) {
    var professions = [];
    if (primaryProfession) {
      professions.push(primaryProfession);
    }
    document.querySelectorAll('.HcpAdditionalProfession:checked').forEach(function (checkbox) {
      if (professions.indexOf(checkbox.value) === -1) {
        professions.push(checkbox.value);
      }
    });
    return professions;
  }

  function buildCustomerNote(profession, professions, license, licenseType) {
    return [
      'hcp_verified',
      'profession:' + profession,
      'professions:' + professions.join(','),
      'license_type:' + licenseType,
      'license:' + license
    ].join('|');
  }

  function buildCustomerTags(professions) {
    var tags = ['hcp-verified'];
    (professions || []).forEach(function (profession) {
      if (profession && tags.indexOf('profession-' + profession) === -1) {
        tags.push('profession-' + profession);
      }
    });
    return tags;
  }

  function buildRegistrationMetafields(profession, license, lastName, licenseType) {
    return {
      licence_no: sanitizeLicense(license),
      select_your_profession: profession,
      last_name: lastName,
      license_number: sanitizeLicense(license),
      profession: profession,
      license_type: licenseType,
      verification_status: 'verified'
    };
  }

  var POST_LOGIN_REDIRECT = '/';

  function sendRegistrationWebhook(payload) {
    var url = window.hcpRegistrationWebhook;
    if (!url) {
      return Promise.resolve(false);
    }

    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      keepalive: true
    }).then(function () {
      return true;
    }).catch(function () {
      return false;
    });
  }

  function getReturnUrl() {
    var params = new URLSearchParams(window.location.search);
    return params.get('return_url') || params.get('checkout_url') || '';
  }

  function showError(container, message) {
    if (!container) return;
    container.textContent = message;
    container.hidden = !message;
  }

  function initRegisterForm() {
    var form = document.getElementById('HcpRegisterForm');
    if (!form) return;

    var professionSelect = document.getElementById('HcpProfession');
    var licenseInput = document.getElementById('HcpLicenseNumber');
    var noteInput = document.getElementById('HcpCustomerNote');
    var errorEl = document.getElementById('HcpRegisterError');
    var warningEl = document.getElementById('HcpRegisterWarning');
    var licenseHint = document.getElementById('HcpLicenseHint');
    var translations = window.hcpAuthStrings || {};

    function t(key) {
      return translations[key] || key;
    }

    function updateHint() {
      if (!licenseHint || !professionSelect) return;
      var profession = professionSelect.value;
      if (profession === 'dietitian') {
        licenseHint.textContent = t('license_hint_adeli');
      } else {
        licenseHint.textContent = t('license_hint_rpps');
      }
    }

    professionSelect && professionSelect.addEventListener('change', updateHint);
    updateHint();

    form.addEventListener('submit', function (event) {
      showError(errorEl, '');
      showError(warningEl, '');

      var profession = professionSelect ? professionSelect.value : '';
      var license = licenseInput ? licenseInput.value : '';

      if (!profession) {
        event.preventDefault();
        showError(errorEl, t('error_profession_required'));
        return;
      }

      var result = validateLicense(license, profession);
      if (!result.valid) {
        event.preventDefault();
        showError(errorEl, t('error_' + result.error) || t('error_invalid_license'));
        return;
      }

      if (result.warning === 'profession_mismatch') {
        showError(warningEl, t('warning_profession_mismatch'));
      }

      if (noteInput) {
        noteInput.value = buildCustomerNote(
          profession,
          collectProfessions(profession),
          sanitizeLicense(license),
          result.type
        );
      }
    });
  }

  function initLoginForm() {
    var form = document.getElementById('HcpLoginForm');
    if (!form) return;

    var returnInput = document.getElementById('HcpReturnTo');
    var returnUrl = getReturnUrl();

    if (returnInput && returnUrl) {
      returnInput.value = returnUrl;
    }
  }

  function buildAuthUrl(baseUrl, email) {
    var url = baseUrl || '/customer_authentication/login';
    if (email) {
      var separator = url.indexOf('?') > -1 ? '&' : '?';
      url += separator + 'login_hint=' + encodeURIComponent(email);
    }
    return url;
  }

  var PENDING_KEY = 'hcp_pending_registration';

  function getVerificationKey(email) {
    return 'hcp_verified_' + String(email || '').toLowerCase().trim();
  }

  function getStoredVerification(email) {
    try {
      var raw = localStorage.getItem(getVerificationKey(email));
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  }

  function setStoredVerification(email, data) {
    try {
      localStorage.setItem(getVerificationKey(email), JSON.stringify(data));
    } catch (e) {
      /* storage unavailable */
    }
  }

  function getPendingRegistration() {
    try {
      var raw = localStorage.getItem(PENDING_KEY) || sessionStorage.getItem(PENDING_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  }

  function clearPendingRegistration() {
    try {
      localStorage.removeItem(PENDING_KEY);
      sessionStorage.removeItem(PENDING_KEY);
    } catch (e) {
      /* storage unavailable */
    }
  }

  function savePendingRegistration(data) {
    try {
      sessionStorage.setItem(PENDING_KEY, JSON.stringify(data));
      localStorage.setItem(PENDING_KEY, JSON.stringify(data));
    } catch (e) {
      /* storage unavailable */
    }
  }

  function markDocumentVerified() {
    document.documentElement.classList.add('hcp-js-verified');
    if (document.body) {
      document.body.classList.add('hcp-js-verified');
    }
  }

  function redirectAfterLogin() {
    var target = POST_LOGIN_REDIRECT;
    if (window.location.pathname !== target) {
      window.location.replace(target);
    }
  }

  function redirectToProfessionPortal(profession) {
    var target = PROFESSION_REDIRECTS[profession] || '/pages/hcp';
    if (window.location.pathname !== target) {
      window.location.replace(target);
    }
  }

  function autoCompleteVerification(email) {
    if (!email) return null;

    var normalizedEmail = email.toLowerCase().trim();
    var verified = getStoredVerification(normalizedEmail);

    if (!verified) {
      var pending = getPendingRegistration();
      if (pending && pending.email && pending.email.toLowerCase().trim() === normalizedEmail) {
        verified = {
          verified: true,
          profession: pending.profession,
          professions: pending.professions || [pending.profession],
          license: pending.license,
          licenseType: pending.licenseType,
          note: pending.note,
          email: pending.email,
          firstName: pending.firstName,
          lastName: pending.lastName
        };
        setStoredVerification(normalizedEmail, verified);
        clearPendingRegistration();
      }
    }

    if (verified && verified.verified) {
      markDocumentVerified();
      window.hcpCustomerData = {
        verified: true,
        profession: verified.profession,
        professions: verified.professions || [verified.profession]
      };
    }

    return verified;
  }

  function initAutoVerification() {
    var customer = window.hcpShopifyCustomer;
    if (!customer || !customer.email) return;

    var verified = autoCompleteVerification(customer.email);
    if (!verified || !verified.verified) return;

    var path = window.location.pathname;
    var isAuthPage = path.indexOf('professional-register') > -1;
    var params = new URLSearchParams(window.location.search);
    var hasReturnUrl = params.has('return_url');

    if (isAuthPage && !hasReturnUrl) {
      redirectAfterLogin();
      return;
    }

    if (params.get('hcp_redirect') === '1' || params.get('hcp_complete') === '1') {
      redirectAfterLogin();
    }
  }

  function initAuthenticatedPages() {
    var target = document.getElementById('hcp-auth-authenticated');
    if (!target) return;

    var email = target.getAttribute('data-hcp-email');
    var serverVerified = target.getAttribute('data-hcp-server-verified') === 'true';

    if (serverVerified) {
      markDocumentVerified();
      return;
    }

    var loadingEl = document.getElementById('hcp-auth-loading');
    var pendingEl = document.getElementById('hcp-pending-fallback');
    var verified = autoCompleteVerification(email);

    if (verified && verified.verified) {
      if (loadingEl) loadingEl.hidden = true;
      redirectAfterLogin();
      return;
    }

    if (loadingEl) loadingEl.hidden = true;
    if (pendingEl) pendingEl.hidden = false;
  }

  function getProfessionLabel(profession) {
    var labels = window.hcpProfessionLabels || {};
    return labels[profession] || profession;
  }

  function initAccountProfessionDisplay() {
    var customer = window.hcpShopifyCustomer;
    if (!customer || !customer.email) return;

    var verified = autoCompleteVerification(customer.email);
    if (!verified || !verified.verified || !verified.profession) return;

    var block = document.getElementById('hcp-account-profession');
    var valueEl = document.getElementById('hcp-account-profession-value');
    var portalLink = document.getElementById('hcp-account-portal-url');

    if (block) block.hidden = false;
    if (valueEl) {
      var labels = (verified.professions || [verified.profession]).map(getProfessionLabel);
      valueEl.textContent = labels.join(', ');
    }
    if (portalLink) {
      portalLink.href = PROFESSION_REDIRECTS[verified.profession] || PROFESSION_REDIRECTS.other;
    }
  }

  function initPendingGates() {
    var gates = document.querySelectorAll('.hcp-gate--pending[data-hcp-customer-email]');
    if (!gates.length) return;

    gates.forEach(function (gate) {
      var email = gate.getAttribute('data-hcp-customer-email');
      var verifyingEl = gate.querySelector('#hcp-gate-verifying');
      var pendingEl = gate.querySelector('#hcp-gate-pending');
      var verified = autoCompleteVerification(email);

      if (verified && verified.verified) {
        gate.hidden = true;
        return;
      }

      if (verifyingEl) verifyingEl.hidden = true;
      if (pendingEl) pendingEl.hidden = false;
    });
  }

  function initProfessionalRegister() {
    var container = document.getElementById('HcpRegisterForm');
    var submitBtn = document.getElementById('HcpProfessionalRegisterSubmit');
    if (!container || !submitBtn) return;

    var professionSelect = document.getElementById('HcpProfession');
    var licenseInput = document.getElementById('HcpLicenseNumber');
    var emailInput = document.getElementById('Email') || document.getElementById('HcpCustomerEmail');
    var firstNameInput = document.getElementById('FirstName') || document.getElementById('HcpCustomerFirstName');
    var lastNameInput = document.getElementById('LastName') || document.getElementById('HcpCustomerLastName');
    var errorEl = document.getElementById('HcpRegisterError');
    var warningEl = document.getElementById('HcpRegisterWarning');
    var licenseHint = document.getElementById('HcpLicenseHint');
    var translations = window.hcpAuthStrings || {};
    var config = window.hcpProfessionalRegister || {};
    var authUrl = container.getAttribute('data-hcp-auth-url') || config.authUrl;

    function t(key) {
      return translations[key] || key;
    }

    function updateHint() {
      if (!licenseHint || !professionSelect) return;
      var profession = professionSelect.value;
      if (profession === 'dietitian') {
        licenseHint.textContent = t('license_hint_adeli');
      } else {
        licenseHint.textContent = t('license_hint_rpps');
      }
    }

    if (professionSelect) {
      professionSelect.addEventListener('change', updateHint);
      updateHint();
    }

    submitBtn.addEventListener('click', function () {
      showError(errorEl, '');
      showError(warningEl, '');

      var profession = professionSelect ? professionSelect.value : '';
      var license = licenseInput ? licenseInput.value : '';
      var email = emailInput ? emailInput.value.trim() : '';
      var firstName = firstNameInput ? firstNameInput.value.trim() : '';
      var lastName = lastNameInput ? lastNameInput.value.trim() : '';

      if (!profession) {
        showError(errorEl, t('error_profession_required'));
        return;
      }

      if (!config.isLoggedIn) {
        if (!firstName || !lastName) {
          showError(errorEl, t('error_name_required'));
          return;
        }
        if (!email) {
          showError(errorEl, t('error_email_required'));
          return;
        }
      }

      var result = validateLicense(license, profession);
      if (!result.valid) {
        showError(errorEl, t('error_' + result.error) || t('error_invalid_license'));
        return;
      }

      if (result.warning === 'profession_mismatch') {
        showError(warningEl, t('warning_profession_mismatch'));
      }

      var professions = collectProfessions(profession);
      var note = buildCustomerNote(
        profession,
        professions,
        sanitizeLicense(license),
        result.type
      );

      var sanitizedLicense = sanitizeLicense(license);
      var registrationPayload = {
        event: config.isLoggedIn ? 'verification_complete' : 'registration_pending',
        email: email,
        firstName: firstName,
        lastName: lastName,
        profession: profession,
        professions: professions,
        tags: buildCustomerTags(professions),
        note: note,
        license: sanitizedLicense,
        licenseType: result.type,
        metafields: buildRegistrationMetafields(profession, sanitizedLicense, lastName, result.type),
        customerId: config.customerId || (window.hcpShopifyCustomer && window.hcpShopifyCustomer.id) || null,
        savedAt: new Date().toISOString()
      };

      savePendingRegistration(registrationPayload);

      sendRegistrationWebhook(registrationPayload);

      if (config.isLoggedIn) {
        setStoredVerification(email, {
          verified: true,
          profession: profession,
          professions: professions,
          license: sanitizeLicense(license),
          licenseType: result.type,
          note: note,
          email: email,
          firstName: firstName,
          lastName: lastName
        });
        clearPendingRegistration();
        markDocumentVerified();
        redirectAfterLogin();
        return;
      }

      window.location.href = buildAuthUrl(authUrl, email);
    });
  }

  function initCustomerTagSync() {
    var customer = window.hcpShopifyCustomer;
    var serverData = window.hcpCustomerData || {};
    if (!customer || !customer.email || serverData.verified) return;

    var params = new URLSearchParams(window.location.search);
    if (params.get('hcp_sync') !== '1') return;

    var verified = autoCompleteVerification(customer.email);
    if (!verified || !verified.verified) return;

    var syncKey = 'hcp_tag_sync_sent_' + customer.email.toLowerCase().trim();
    if (sessionStorage.getItem(syncKey) === '1') return;

    var professions = verified.professions || [verified.profession];
    sendRegistrationWebhook({
      event: 'registration_complete',
      email: customer.email,
      firstName: verified.firstName || '',
      lastName: verified.lastName || '',
      profession: verified.profession,
      professions: professions,
      tags: buildCustomerTags(professions),
      note: verified.note,
      license: verified.license,
      licenseType: verified.licenseType,
      metafields: buildRegistrationMetafields(
        verified.profession,
        verified.license,
        verified.lastName || '',
        verified.licenseType
      ),
      customerId: customer.id,
      savedAt: new Date().toISOString()
    }).then(function () {
      sessionStorage.setItem(syncKey, '1');
    });
  }

  function initAccountRedirect() {
    var params = new URLSearchParams(window.location.search);
    var forceRedirect = params.get('hcp_redirect') === '1' || params.get('hcp_complete') === '1';
    if (!forceRedirect) return;

    var redirectKey = 'hcp_post_login_redirected';
    if (sessionStorage.getItem(redirectKey) === '1') return;

    sessionStorage.setItem(redirectKey, '1');
    redirectAfterLogin();
  }

  window.HcpAuth = {
    validateLicense: validateLicense,
    sanitizeLicense: sanitizeLicense,
    buildCustomerTags: buildCustomerTags,
    professionRedirects: PROFESSION_REDIRECTS,
    getStoredVerification: getStoredVerification,
    autoCompleteVerification: autoCompleteVerification,
    sendRegistrationWebhook: sendRegistrationWebhook
  };

  document.addEventListener('DOMContentLoaded', function () {
    initAutoVerification();
    initAuthenticatedPages();
    initPendingGates();
    initAccountProfessionDisplay();
    initRegisterForm();
    initLoginForm();
    initProfessionalRegister();
    initCustomerTagSync();
    initAccountRedirect();
  });
})();
